import React, { useState } from 'react';
import { Menu, X, Star, Check, Phone, Mail, MapPin } from 'lucide-react';

const App = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navigation = [
    { name: 'Home', page: 'home' },
    { name: 'Services', page: 'services' },
    { name: 'About Us', page: 'about' },
    { name: 'Contact Us', page: 'contact' }
  ];

  const Header = () => (
    <header className="fixed w-full top-0 z-50 bg-black/95 backdrop-blur-sm border-b border-orange-500/20">
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 sm:h-20">
          <div className="flex items-center">
            <span className="text-orange-500 text-xl sm:text-2xl font-bold">Freedom Christ</span>
          </div>
          
          <div className="hidden md:flex space-x-8">
            {navigation.map((item) => (
              <button
                key={item.name}
                onClick={() => setCurrentPage(item.page)}
                className={`text-sm lg:text-base transition-colors ${
                  currentPage === item.page ? 'text-orange-500' : 'text-white hover:text-orange-500'
                }`}
              >
                {item.name}
              </button>
            ))}
          </div>

          {/* <button className="hidden md:block bg-orange-500 text-white px-4 lg:px-6 py-2 rounded-lg hover:bg-orange-600 transition-colors text-sm lg:text-base">
            Login
          </button> */}

          <button
            className="md:hidden text-white"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden pb-4">
            {navigation.map((item) => (
              <button
                key={item.name}
                onClick={() => {
                  setCurrentPage(item.page);
                  setMobileMenuOpen(false);
                }}
                className={`block w-full text-left py-2 transition-colors ${
                  currentPage === item.page ? 'text-orange-500' : 'text-white hover:text-orange-500'
                }`}
              >
                {item.name}
              </button>
            ))}
            {/* <button className="w-full mt-4 bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600 transition-colors">
              Login
            </button> */}
          </div>
        )}
      </nav>
    </header>
  );

  const Footer = () => (
    <footer className="bg-black border-t border-gray-800 py-8 sm:py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          <div>
            <h3 className="text-orange-500 text-lg sm:text-xl font-bold mb-4">Freedom Christ</h3>
            <p className="text-gray-400 text-sm">Automate your real estate lead generation 24/7 with AI-powered technology.</p>
          </div>
          
          <div>
            <h4 className="text-white font-semibold mb-4">Services</h4>
            <ul className="space-y-2">
              <li><button onClick={() => setCurrentPage('services')} className="text-gray-400 hover:text-orange-500 text-sm">AI Lead Generation</button></li>
              <li><button onClick={() => setCurrentPage('services')} className="text-gray-400 hover:text-orange-500 text-sm">24/7 Call Automation</button></li>
              <li><button onClick={() => setCurrentPage('services')} className="text-gray-400 hover:text-orange-500 text-sm">CRM Integration</button></li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-white font-semibold mb-4">Legal</h4>
            <ul className="space-y-2">
              <li><button onClick={() => setCurrentPage('terms')} className="text-gray-400 hover:text-orange-500 text-sm">Terms and Conditions</button></li>
              <li><button onClick={() => setCurrentPage('privacy')} className="text-gray-400 hover:text-orange-500 text-sm">Privacy Policy</button></li>
              <li><button onClick={() => setCurrentPage('refund')} className="text-gray-400 hover:text-orange-500 text-sm">Refund Policy</button></li>
            </ul>
          </div>
          
          <div>
            <h4 className="text-white font-semibold mb-4">Contact</h4>
            <ul className="space-y-2">
              <li><button onClick={() => setCurrentPage('contact')} className="text-gray-400 hover:text-orange-500 text-sm">Contact Us</button></li>
              <li><button onClick={() => setCurrentPage('about')} className="text-gray-400 hover:text-orange-500 text-sm">About Us</button></li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400 text-sm">
          <p>Copyright 2025 Freedom Christ LLC. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );

  const HomePage = () => (
    <div className="bg-black text-white">
      <section className="min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-20">
        <div className="text-center max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold mb-6">
            Automate Your Real Estate Lead Generation <span className="text-orange-500">24/7</span>
          </h1>
          <p className="text-gray-400 text-base sm:text-lg lg:text-xl mb-8 max-w-2xl mx-auto">
            Let AI agents handle lead capture, qualification, and follow-up automatically while you focus on closing deals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => setCurrentPage('contact')} className="bg-orange-500 text-white px-6 sm:px-8 py-3 rounded-lg hover:bg-orange-600 transition-colors text-sm sm:text-base">
              Start Free Trial
            </button>
            <button onClick={() => setCurrentPage('contact')} className="border border-orange-500 text-orange-500 px-6 sm:px-8 py-3 rounded-lg hover:bg-orange-500 hover:text-white transition-colors text-sm sm:text-base">
              Schedule Demo
            </button>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-center mb-12 sm:mb-16">Powerful AI Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {[
              { title: 'Intelligent Lead Generation', desc: 'AI-powered system captures and qualifies leads automatically from multiple sources.' },
              { title: '24/7 Call Automation', desc: 'Never miss a lead with automated calling and follow-up at any time of day.' },
              { title: 'CRM Recording and Analytics', desc: 'Seamlessly integrate with your CRM and get detailed analytics on lead performance.' },
              { title: 'Automatic Scheduling', desc: 'Let AI handle appointment scheduling and calendar management automatically.' },
              { title: 'Real-Time Insights', desc: 'Get instant notifications and insights on lead behavior and engagement.' },
              { title: 'CRM Integration', desc: 'Works with all major CRM platforms for seamless workflow integration.' }
            ].map((feature, idx) => (
              <div key={idx} className="bg-gray-900 p-6 sm:p-8 rounded-lg border border-gray-800 hover:border-orange-500 transition-all">
                <div className="text-orange-500 mb-4">
                  <div className="w-12 h-12 bg-orange-500/10 rounded-lg flex items-center justify-center">
                    <Check size={24} />
                  </div>
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-3">{feature.title}</h3>
                <p className="text-gray-400 text-sm sm:text-base">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-gray-900/50">
        <div className="container mx-auto">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-center mb-12 sm:mb-16">Why Choose Freedom Christ?</h2>
          <div className="max-w-3xl mx-auto space-y-6">
            {[
              'Save 20+ hours per week on lead management',
              'Increase lead quality with AI-powered qualification',
              'Never miss a follow-up with automated scheduling',
              'Scale your real estate business without adding staff',
              'Get detailed analytics on all your lead sources'
            ].map((reason, idx) => (
              <div key={idx} className="flex items-start gap-4 bg-black/50 p-4 sm:p-6 rounded-lg border border-gray-800">
                <Check className="text-orange-500 flex-shrink-0 mt-1" size={20} />
                <p className="text-base sm:text-lg">{reason}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-center mb-4">Simple, Transparent Pricing</h2>
          <p className="text-center text-gray-400 mb-12 sm:mb-16 text-sm sm:text-base">Choose the plan that fits your business needs</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto">
            {[
              { 
                name: 'Starter', 
                price: '$99', 
                features: ['Up to 50 leads per month', 'Basic AI call automation', 'Email support', 'CRM integration', 'Basic analytics']
              },
              { 
                name: 'Professional', 
                price: '$299', 
                features: ['Up to 200 leads per month', 'Advanced AI automation', '24/7 priority support', 'All CRM integrations', 'Advanced analytics', 'Custom workflows', 'Team collaboration'],
                popular: true
              },
              { 
                name: 'Enterprise', 
                price: '$599', 
                features: ['Unlimited leads', 'Full AI automation suite', 'Dedicated account manager', 'Custom integrations', 'White-label options', 'API access', 'Priority onboarding']
              }
            ].map((plan, idx) => (
              <div key={idx} className={`bg-gray-900 p-6 sm:p-8 rounded-lg border-2 ${plan.popular ? 'border-orange-500' : 'border-gray-800'} relative`}>
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-orange-500 text-white px-4 py-1 rounded-full text-xs sm:text-sm">
                    Most Popular
                  </div>
                )}
                <h3 className="text-xl sm:text-2xl font-bold mb-2">{plan.name}</h3>
                <div className="mb-6">
                  <span className="text-3xl sm:text-4xl font-bold">{plan.price}</span>
                  <span className="text-gray-400 text-sm sm:text-base">/month</span>
                </div>
                <button onClick={() => setCurrentPage('contact')} className={`w-full py-3 rounded-lg mb-6 transition-colors text-sm sm:text-base ${
                  plan.popular 
                    ? 'bg-orange-500 text-white hover:bg-orange-600' 
                    : 'border border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white'
                }`}>
                  Get Started
                </button>
                <ul className="space-y-3">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm sm:text-base">
                      <Check className="text-orange-500 flex-shrink-0 mt-1" size={16} />
                      <span className="text-gray-300">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-gray-900/50">
        <div className="container mx-auto">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-center mb-12 sm:mb-16">Loved by Real Estate Professionals</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 max-w-6xl mx-auto">
            {[
              { name: 'Sarah Johnson', role: 'Real Estate Agent, Century 21', text: 'Freedom Christ has completely transformed how I handle leads. I am closing 40% more deals!' },
              { name: 'Mike Thompson', role: 'Broker, RE/MAX', text: 'The AI automation is incredible. It is like having a full team working 24/7 for a fraction of the cost.' },
              { name: 'Robert Chen', role: 'Independent Agent', text: 'I was skeptical at first, but the results speak for themselves. Best investment I have made in my business.' }
            ].map((testimonial, idx) => (
              <div key={idx} className="bg-black p-6 sm:p-8 rounded-lg border border-gray-800">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} className="fill-orange-500 text-orange-500" />
                  ))}
                </div>
                <p className="text-gray-300 mb-4 text-sm sm:text-base">{testimonial.text}</p>
                <div>
                  <p className="font-semibold text-sm sm:text-base">{testimonial.name}</p>
                  <p className="text-gray-400 text-xs sm:text-sm">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-4xl">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-center mb-12 sm:mb-16">Frequently Asked Questions</h2>
          <div className="space-y-6">
            {[
              { q: 'How does the AI agent generate leads?', a: 'Our AI system integrates with multiple lead sources including your website, social media, and third-party platforms. It automatically captures, qualifies, and nurtures leads using intelligent conversations and follow-up sequences.' },
              { q: 'How long does setup take?', a: 'Setup typically takes 24-48 hours. Our team will help you integrate with your existing CRM and configure the AI to match your specific business needs and qualifying criteria.' },
              { q: 'Can I customize the AI scripts?', a: 'Yes! You have full control over how the AI interacts with your leads. You can customize scripts, qualifying questions, and follow-up sequences to match your brand voice and sales process.' },
              { q: 'How do I integrate with my CRM?', a: 'We support all major CRM platforms including Salesforce, HubSpot, and Zoho. Our integration is seamless and automatically syncs all lead data, call logs, and analytics to your existing system.' },
              { q: 'Will I need support?', a: 'We provide comprehensive onboarding and 24/7 support for all plans. Our team is always available to help you optimize your AI agent and maximize your ROI.' },
              { q: 'Is there a refund?', a: 'Yes, we offer a 30-day money-back guarantee. If you are not satisfied with the results, we will provide a full refund, no questions asked.' }
            ].map((faq, idx) => (
              <div key={idx} className="bg-gray-900 p-6 sm:p-8 rounded-lg border border-gray-800">
                <h3 className="text-lg sm:text-xl font-semibold mb-3">{faq.q}</h3>
                <p className="text-gray-400 text-sm sm:text-base">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-orange-500/20 to-red-500/20">
        <div className="container mx-auto text-center max-w-3xl">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">Ready to Transform Your Business?</h2>
          <p className="text-gray-300 text-base sm:text-lg mb-8">Join thousands of real estate professionals who are automating their lead generation with AI.</p>
          <button onClick={() => setCurrentPage('contact')} className="bg-orange-500 text-white px-8 sm:px-12 py-3 sm:py-4 rounded-lg hover:bg-orange-600 transition-colors text-base sm:text-lg font-semibold">
            Start Your Free Trial
          </button>
        </div>
      </section>
    </div>
  );

  const ServicesPage = () => (
    <div className="bg-black text-white min-h-screen pt-20 sm:pt-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-center">Our Services</h1>
        <p className="text-gray-400 text-center mb-12 sm:mb-16 max-w-3xl mx-auto text-base sm:text-lg">
          Comprehensive AI-powered solutions designed to transform your real estate lead generation
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 max-w-6xl mx-auto">
          {[
            {
              title: 'AI Lead Generation',
              description: 'Our intelligent AI system works around the clock to capture and qualify leads from multiple sources. It analyzes lead behavior, engagement patterns, and qualification criteria to ensure you only spend time on high-quality prospects.',
              features: ['Multi-channel lead capture', 'Intelligent lead scoring', 'Automated qualification', 'Real-time notifications']
            },
            {
              title: '24/7 Call Automation',
              description: 'Never miss an opportunity with our automated calling system. Our AI handles initial contact, qualification calls, and follow-ups automatically, ensuring every lead receives prompt attention regardless of time or day.',
              features: ['Automated outbound calling', 'Intelligent voicemail detection', 'Call recording and transcription', 'Follow-up scheduling']
            },
            {
              title: 'CRM Integration',
              description: 'Seamlessly connect with your existing CRM platform. All lead data, call logs, and interactions are automatically synced, giving you a complete view of your sales pipeline without manual data entry.',
              features: ['Works with major CRMs', 'Automatic data sync', 'Custom field mapping', 'Two-way integration']
            },
            {
              title: 'Advanced Analytics',
              description: 'Get deep insights into your lead generation performance. Track conversion rates, lead sources, engagement metrics, and ROI with our comprehensive analytics dashboard.',
              features: ['Real-time dashboards', 'Custom reports', 'Lead source tracking', 'Conversion analytics']
            },
            {
              title: 'Appointment Scheduling',
              description: 'Let AI handle your calendar management. The system automatically schedules appointments based on your availability, sends reminders, and handles rescheduling requests.',
              features: ['Calendar integration', 'Automated reminders', 'Conflict prevention', 'Time zone handling']
            },
            {
              title: 'Custom Workflows',
              description: 'Create personalized automation workflows that match your unique sales process. Design custom follow-up sequences, qualification paths, and nurture campaigns.',
              features: ['Drag-and-drop builder', 'Conditional logic', 'A/B testing', 'Performance tracking']
            }
          ].map((service, idx) => (
            <div key={idx} className="bg-gray-900 p-6 sm:p-8 rounded-lg border border-gray-800 hover:border-orange-500 transition-all">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-orange-500">{service.title}</h2>
              <p className="text-gray-300 mb-6 text-sm sm:text-base">{service.description}</p>
              <ul className="space-y-3">
                {service.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm sm:text-base">
                    <Check className="text-orange-500 flex-shrink-0 mt-1" size={16} />
                    <span className="text-gray-400">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-16 sm:mt-20 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6">Ready to Get Started?</h2>
          <p className="text-gray-400 mb-8 text-sm sm:text-base">Let us show you how our services can transform your business</p>
          <button onClick={() => setCurrentPage('contact')} className="bg-orange-500 text-white px-8 py-3 rounded-lg hover:bg-orange-600 transition-colors text-sm sm:text-base">
            Schedule a Demo
          </button>
        </div>
      </div>
    </div>
  );

  const AboutPage = () => (
    <div className="bg-black text-white min-h-screen pt-20 sm:pt-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-center">About Freedom Christ</h1>
        <p className="text-gray-400 text-center mb-12 sm:mb-16 max-w-3xl mx-auto text-base sm:text-lg">
          Revolutionizing real estate lead generation with cutting-edge AI technology
        </p>

        <div className="max-w-4xl mx-auto space-y-12 sm:space-y-16">
          <section className="bg-gray-900 p-6 sm:p-10 rounded-lg border border-gray-800">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-orange-500">Our Mission</h2>
            <p className="text-gray-300 leading-relaxed text-sm sm:text-base">
              At Freedom Christ, we believe that real estate professionals should spend their time doing what they do best - closing deals and building relationships. Our mission is to eliminate the time-consuming tasks of lead capture, qualification, and follow-up by leveraging advanced AI technology. We are committed to helping agents and brokers scale their businesses without adding overhead, allowing them to achieve true freedom in how they work.
            </p>
          </section>

          <section className="bg-gray-900 p-6 sm:p-10 rounded-lg border border-gray-800">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-orange-500">Our Story</h2>
            <p className="text-gray-300 leading-relaxed mb-4 text-sm sm:text-base">
              Freedom Christ was founded by a team of real estate professionals and AI engineers who experienced firsthand the challenges of managing leads in a competitive market. After years of struggling with missed opportunities, time-consuming follow-ups, and inefficient processes, we knew there had to be a better way.
            </p>
            <p className="text-gray-300 leading-relaxed text-sm sm:text-base">
              We combined our expertise in real estate and artificial intelligence to create a solution that does not just automate tasks - it intelligently manages your entire lead generation process. Today, we are proud to serve thousands of real estate professionals who are experiencing unprecedented growth and efficiency in their businesses.
            </p>
          </section>

          <section className="bg-gray-900 p-6 sm:p-10 rounded-lg border border-gray-800">
            <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-orange-500">Our Values</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                { title: 'Innovation', desc: 'We continuously push the boundaries of what AI can do for real estate professionals.' },
                { title: 'Reliability', desc: 'Our systems work 24/7 with 99.9% uptime, so you never miss an opportunity.' },
                { title: 'Transparency', desc: 'We provide clear pricing, honest communication, and detailed analytics.' },
                { title: 'Success', desc: 'Your success is our success. We measure ourselves by the results you achieve.' }
              ].map((value, idx) => (
                <div key={idx} className="bg-black/50 p-6 rounded-lg border border-gray-700">
                  <h3 className="text-lg sm:text-xl font-semibold mb-2 text-orange-500">{value.title}</h3>
                  <p className="text-gray-400 text-sm sm:text-base">{value.desc}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="bg-gray-900 p-6 sm:p-10 rounded-lg border border-gray-800">
            <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-orange-500">Why Choose Us</h2>
            <div className="space-y-4">
              {[
                'Industry-leading AI technology built specifically for real estate',
                'Proven track record with thousands of successful users',
                '24/7 customer support from real estate and tech experts',
                'Continuous innovation and feature updates',
                'Seamless integration with existing tools and workflows',
                'Transparent pricing with no hidden fees'
              ].map((reason, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <Check className="text-orange-500 flex-shrink-0 mt-1" size={20} />
                  <p className="text-gray-300 text-sm sm:text-base">{reason}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="text-center bg-gradient-to-r from-orange-500/20 to-red-500/20 p-8 sm:p-12 rounded-lg">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4">Join Our Growing Community</h2>
            <p className="text-gray-300 mb-8 text-sm sm:text-base">Experience the future of real estate lead generation</p>
            <button onClick={() => setCurrentPage('contact')} className="bg-orange-500 text-white px-8 py-3 rounded-lg hover:bg-orange-600 transition-colors text-sm sm:text-base">
              Get Started Today
            </button>
          </section>
        </div>
      </div>
    </div>
  );

  const ContactPage = () => {
    const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });

    const handleSubmit = (e) => {
      e.preventDefault();
      alert('Thank you for your message! We will get back to you soon.');
      setFormData({ name: '', email: '', phone: '', message: '' });
    };

    return (
      <div className="bg-black text-white min-h-screen pt-20 sm:pt-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-center">Get in Touch</h1>
          <p className="text-gray-400 text-center mb-12 sm:mb-16 max-w-3xl mx-auto text-base sm:text-lg">
            Have questions? We would love to hear from you. Send us a message and we will respond as soon as possible.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-orange-500">Contact Information</h2>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4 bg-gray-900 p-6 rounded-lg border border-gray-800">
                  <Phone className="text-orange-500 flex-shrink-0" size={24} />
                  <div>
                    <h3 className="font-semibold mb-1 text-base sm:text-lg">Phone</h3>
                    <p className="text-gray-400 text-sm sm:text-base">+1 (914) 436-5542</p>
                    <p className="text-gray-500 text-xs sm:text-sm mt-1">Mon-Fri 9am-6pm EST</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-gray-900 p-6 rounded-lg border border-gray-800">
                  <Mail className="text-orange-500 flex-shrink-0" size={24} />
                  <div>
                    <h3 className="font-semibold mb-1 text-base sm:text-lg">Email</h3>
                    <p className="text-gray-400 text-sm sm:text-base">support@freedomchrist.site</p>
                    <p className="text-gray-500 text-xs sm:text-sm mt-1">We will respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 bg-gray-900 p-6 rounded-lg border border-gray-800">
                  <MapPin className="text-orange-500 flex-shrink-0" size={24} />
                  <div>
                    <h3 className="font-semibold mb-1 text-base sm:text-lg">Address</h3>
                    <p className="text-gray-400 text-sm sm:text-base">1001 S MAIN ST STE 500</p>
                    <p className="text-gray-400 text-sm sm:text-base">KALISPELL, MT 599011</p>
                  </div>
                </div>
              </div>

              <div className="mt-8 bg-gray-900 p-6 rounded-lg border border-gray-800">
                <h3 className="text-xl font-bold mb-4 text-orange-500">Business Hours</h3>
                <div className="space-y-2 text-sm sm:text-base">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Monday - Friday</span>
                    <span className="text-white">9:00 AM - 6:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Saturday</span>
                    <span className="text-white">10:00 AM - 4:00 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Sunday</span>
                    <span className="text-white">Closed</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-900 p-6 sm:p-8 rounded-lg border border-gray-800">
              <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-orange-500">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:outline-none focus:border-orange-500 text-white text-sm sm:text-base"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:outline-none focus:border-orange-500 text-white text-sm sm:text-base"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:outline-none focus:border-orange-500 text-white text-sm sm:text-base"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Message</label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    rows={5}
                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded-lg focus:outline-none focus:border-orange-500 text-white text-sm sm:text-base"
                    required
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 transition-colors font-semibold text-sm sm:text-base"
                >
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const TermsPage = () => (
    <div className="bg-black text-white min-h-screen pt-20 sm:pt-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 max-w-4xl">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-center">Terms and Conditions</h1>
        <p className="text-gray-400 text-center mb-12 text-sm sm:text-base">Last Updated: November 20, 2025</p>

        <div className="space-y-8 text-gray-300">
          <section className="bg-gray-900 p-6 sm:p-8 rounded-lg border border-gray-800">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 text-orange-500">1. Acceptance of Terms</h2>
            <p className="text-sm sm:text-base leading-relaxed">
              By accessing and using Freedom Christ services, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to these terms, please do not use our services.
            </p>
          </section>

          <section className="bg-gray-900 p-6 sm:p-8 rounded-lg border border-gray-800">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 text-orange-500">2. Service Description</h2>
            <p className="text-sm sm:text-base leading-relaxed mb-4">
              Freedom Christ provides AI-powered lead generation and automation services for real estate professionals. Our services include but are not limited to automated lead capture and qualification, 24/7 call automation and follow-up systems, CRM integration and data synchronization, and analytics and reporting tools.
            </p>
          </section>

          <section className="bg-gray-900 p-6 sm:p-8 rounded-lg border border-gray-800">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 text-orange-500">3. User Obligations</h2>
            <p className="text-sm sm:text-base leading-relaxed mb-4">
              As a user of our services, you agree to provide accurate and complete information during registration, maintain the security of your account credentials, use the service in compliance with all applicable laws and regulations, and not attempt to reverse engineer or compromise our systems.
            </p>
          </section>

          <section className="bg-gray-900 p-6 sm:p-8 rounded-lg border border-gray-800">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 text-orange-500">4. Payment Terms</h2>
            <p className="text-sm sm:text-base leading-relaxed mb-4">
              Subscription fees are billed monthly or annually based on your selected plan. All fees are non-refundable except as provided in our 30-day money-back guarantee for new customers. We reserve the right to change our pricing with 30 days notice to existing customers.
            </p>
          </section>

          <section className="bg-gray-900 p-6 sm:p-8 rounded-lg border border-gray-800">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 text-orange-500">5. Intellectual Property</h2>
            <p className="text-sm sm:text-base leading-relaxed">
              All content, features, and functionality of Freedom Christ services are owned by us and are protected by international copyright, trademark, and other intellectual property laws. You may not copy, modify, or distribute our content without explicit written permission.
            </p>
          </section>

          <section className="bg-gray-900 p-6 sm:p-8 rounded-lg border border-gray-800">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 text-orange-500">6. Limitation of Liability</h2>
            <p className="text-sm sm:text-base leading-relaxed">
              Freedom Christ shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use or inability to use the service. Our total liability shall not exceed the amount paid by you in the twelve months preceding the claim.
            </p>
          </section>

          <section className="bg-gray-900 p-6 sm:p-8 rounded-lg border border-gray-800">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 text-orange-500">7. Termination</h2>
            <p className="text-sm sm:text-base leading-relaxed">
              We reserve the right to terminate or suspend your account at any time for violation of these terms. You may cancel your subscription at any time through your account settings. Upon termination, your right to use the service will immediately cease.
            </p>
          </section>

          <section className="bg-gray-900 p-6 sm:p-8 rounded-lg border border-gray-800">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 text-orange-500">8. Changes to Terms</h2>
            <p className="text-sm sm:text-base leading-relaxed">
              We reserve the right to modify these terms at any time. We will notify users of any material changes via email or through our service. Your continued use of the service after such modifications constitutes your acceptance of the updated terms.
            </p>
          </section>

          <section className="bg-gray-900 p-6 sm:p-8 rounded-lg border border-gray-800">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 text-orange-500">9. Contact Information</h2>
            <p className="text-sm sm:text-base leading-relaxed">
              If you have any questions about these Terms and Conditions, please contact us at legal@freedomchrist.site or call us at +1 (914) 436-5542.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
  const RefundPage = () => (
  <div className="bg-black text-white min-h-screen pt-20 sm:pt-24">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 max-w-4xl">
      <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-center">Refund Policy</h1>
      <p className="text-gray-400 text-center mb-12 text-sm sm:text-base">Last Updated: November 20, 2025</p>

      <div className="space-y-8 text-gray-300">
        <section className="bg-gray-900 p-6 sm:p-8 rounded-lg border border-gray-800">
          <h2 className="text-xl sm:text-2xl font-bold mb-4 text-orange-500">1. Refund Eligibility</h2>
          <p className="text-sm sm:text-base leading-relaxed">
            Refunds are available only for new customers who have subscribed to our services and are not satisfied with the service provided. To request a refund, you must contact us within 30 days of your initial subscription.
          </p>
        </section>

        <section className="bg-gray-900 p-6 sm:p-8 rounded-lg border border-gray-800">
          <h2 className="text-xl sm:text-2xl font-bold mb-4 text-orange-500">2. Non-Refundable Services</h2>
          <p className="text-sm sm:text-base leading-relaxed">
            Certain services, including ongoing monthly subscriptions beyond the first 30 days, one-time setup fees, and additional add-ons purchased after the initial subscription, are non-refundable. Please review your subscription details carefully.
          </p>
        </section>

        <section className="bg-gray-900 p-6 sm:p-8 rounded-lg border border-gray-800">
          <h2 className="text-xl sm:text-2xl font-bold mb-4 text-orange-500">3. Refund Process</h2>
          <p className="text-sm sm:text-base leading-relaxed mb-2">
            To request a refund, please contact our support team at <span className="text-orange-500">support@freedomchrist.site</span> with your subscription details and reason for the request.
          </p>
          <p className="text-sm sm:text-base leading-relaxed">
            Once we receive your request, we will review it and notify you of the approval or rejection of your refund within 5-7 business days.
          </p>
        </section>

        <section className="bg-gray-900 p-6 sm:p-8 rounded-lg border border-gray-800">
          <h2 className="text-xl sm:text-2xl font-bold mb-4 text-orange-500">4. Refund Method</h2>
          <p className="text-sm sm:text-base leading-relaxed">
            Approved refunds will be processed back to the original payment method used during the subscription. Please allow 5-10 business days for the refunded amount to appear in your account, depending on your bank or payment provider.
          </p>
        </section>

        <section className="bg-gray-900 p-6 sm:p-8 rounded-lg border border-gray-800">
          <h2 className="text-xl sm:text-2xl font-bold mb-4 text-orange-500">5. Contact Information</h2>
          <p className="text-sm sm:text-base leading-relaxed">
            If you have any questions regarding this Refund Policy, please contact us at:
          </p>
          <ul className="mt-2 space-y-2 text-sm sm:text-base">
            <li>Email: <span className="text-orange-500">support@freedomchrist.site</span></li>
            <li>Phone: +1 (914) 436-5542</li>
          </ul>
        </section>
      </div>
    </div>
  </div>
);


  const PrivacyPage = () => (
    <div className="bg-black text-white min-h-screen pt-20 sm:pt-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 max-w-4xl">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-center">Privacy Policy</h1>
        <p className="text-gray-400 text-center mb-12 text-sm sm:text-base">Last Updated: November 20, 2025</p>

        <div className="space-y-8 text-gray-300">
          <section className="bg-gray-900 p-6 sm:p-8 rounded-lg border border-gray-800">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 text-orange-500">1. Information We Collect</h2>
            <p className="text-sm sm:text-base leading-relaxed mb-4">
              We collect information that you provide directly to us, including account information such as name, email address, and phone number, business information including company name and real estate license details, lead data that you upload or generate through our platform, and usage data and analytics about how you interact with our services.
            </p>
          </section>

          <section className="bg-gray-900 p-6 sm:p-8 rounded-lg border border-gray-800">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 text-orange-500">2. How We Use Your Information</h2>
            <p className="text-sm sm:text-base leading-relaxed mb-4">
              We use the information we collect to provide, maintain, and improve our services, process your transactions and send related information, send you technical notices, updates, and support messages, respond to your comments and questions, and analyze usage patterns to improve user experience.
            </p>
          </section>

          <section className="bg-gray-900 p-6 sm:p-8 rounded-lg border border-gray-800">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 text-orange-500">3. Information Sharing</h2>
            <p className="text-sm sm:text-base leading-relaxed mb-4">
              We do not sell your personal information. We may share your information only with your consent or at your direction, with service providers who perform services on our behalf, to comply with legal obligations or protect rights and safety, and in connection with a merger, acquisition, or sale of assets.
            </p>
          </section>

          <section className="bg-gray-900 p-6 sm:p-8 rounded-lg border border-gray-800">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 text-orange-500">4. Data Security</h2>
            <p className="text-sm sm:text-base leading-relaxed">
              We implement industry-standard security measures to protect your information from unauthorized access, alteration, disclosure, or destruction. This includes encryption of data in transit and at rest, regular security audits, and strict access controls. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section className="bg-gray-900 p-6 sm:p-8 rounded-lg border border-gray-800">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 text-orange-500">5. Data Retention</h2>
            <p className="text-sm sm:text-base leading-relaxed">
              We retain your information for as long as your account is active or as needed to provide you services. We will also retain and use your information as necessary to comply with legal obligations, resolve disputes, and enforce our agreements. You may request deletion of your data at any time by contacting us.
            </p>
          </section>

          <section className="bg-gray-900 p-6 sm:p-8 rounded-lg border border-gray-800">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 text-orange-500">6. Your Rights</h2>
            <p className="text-sm sm:text-base leading-relaxed mb-4">
              You have the right to access and receive a copy of your personal data, correct inaccurate or incomplete data, request deletion of your data, object to or restrict certain processing activities, and data portability to transfer your data to another service.
            </p>
          </section>

          <section className="bg-gray-900 p-6 sm:p-8 rounded-lg border border-gray-800">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 text-orange-500">7. Cookies and Tracking</h2>
            <p className="text-sm sm:text-base leading-relaxed">
              We use cookies and similar tracking technologies to collect information about your browsing activities. You can control cookies through your browser settings, but disabling cookies may limit your ability to use certain features of our service.
            </p>
          </section>

          <section className="bg-gray-900 p-6 sm:p-8 rounded-lg border border-gray-800">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 text-orange-500">8. Changes to This Policy</h2>
            <p className="text-sm sm:text-base leading-relaxed">
              We may update this privacy policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the last updated date. We encourage you to review this policy periodically.
            </p>
          </section>

          <section className="bg-gray-900 p-6 sm:p-8 rounded-lg border border-gray-800">
            <h2 className="text-xl sm:text-2xl font-bold mb-4 text-orange-500">9. Contact Us</h2>
            <p className="text-sm sm:text-base leading-relaxed">
              If you have questions about this Privacy Policy or our data practices, please contact us at privacy@freedomchrist.site or call us at +1 (914) 436-5542.
            </p>
          </section>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-black">
      <Header />
      {currentPage === 'home' && <HomePage />}
      {currentPage === 'services' && <ServicesPage />}
      {currentPage === 'about' && <AboutPage />}
      {currentPage === 'contact' && <ContactPage />}
      {currentPage === 'terms' && <TermsPage />}
      {currentPage === 'privacy' && <PrivacyPage />}
      {currentPage === 'refund' && <RefundPage />}
      <Footer />
    </div>
  );
};

export default App;